import { GIT_HOST_CONFIGS, type GitProvider } from "@/lib/repoSource/gitHosts";

export type ParsedGitRepoUrl = {
  provider: GitProvider;
  host: string;
  owner: string;
  repo: string;
};

/**
 * Parse a user-typed repository reference into its canonical
 * `{ provider, host, owner, repo }` for the `/<host>/<owner>/<repo>` intake
 * route, trying every allowlisted host (github.com, codeberg.org).
 *
 * Accepted shapes (owner/repo only — deeper paths are not a repo root and
 * return null, matching App.tsx's `githubRepoFromPath`/`codebergRepoFromPath`
 * segment rules):
 *   github.com/owner/repo
 *   https://github.com/owner/repo
 *   http://github.com/owner/repo (upgraded)
 *   www.github.com/owner/repo
 *   …and the same shapes for codeberg.org.
 *   …with a trailing slash and/or a `.git` suffix on the repo segment.
 */
export function parseGitRepoUrl(input: string): ParsedGitRepoUrl | null {
  const raw = input.trim();
  if (!raw) return null;

  // Strip scheme and leading www. — everything else must be exactly
  // `<host>/<owner>/<repo>`.
  const withoutScheme = raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  for (const config of Object.values(GIT_HOST_CONFIGS)) {
    const escapedHost = config.host.replace(/\./g, "\\.");
    const match = withoutScheme.match(
      new RegExp(`^${escapedHost}/([A-Za-z0-9_.-]+)/([A-Za-z0-9_.-]+)/?$`, "i"),
    );
    if (match) {
      return {
        provider: config.provider,
        host: config.host,
        owner: match[1],
        repo: match[2].replace(/\.git$/i, ""),
      };
    }
  }
  return null;
}
